import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import type { components } from '@/types/api.generated';
import { toApiListParams, type ListRequestParams } from './list-request';
import { ORDER_FILTERS } from './filters.generated';

// The `/api/orders` filter whitelist is codegen from the backend
// `orderListConfig.filterable` via the OpenAPI `x-filterable` extension
// (issue #1407). Re-exported here so the `@/services/orders` import path is
// unchanged; edit the backend config, not this file. Filters outside it
// (e.g. the UI's `has_onfleet_task`, no backend column) are dropped by
// `toApiListParams` rather than 400-ing the page.
export { ORDER_FILTERS };

/**
 * Nested order detail wire shape returned by `GET /api/orders/{id}`
 * (order-routes `orderDetailSchema`): the summary row plus verbatim
 * Shopify line-item objects and the split preview. Consumed from the
 * generated OpenAPI types so a backend contract change surfaces at
 * typecheck.
 */
export type OrderDetail = components['schemas']['OrderDetail'];

/**
 * Fulfillment-job wire shape returned by `GET /api/fulfillment-jobs`
 * (delivery read surface). Replaces the archived `/orders/:id/tasks`
 * per-order lookup.
 */
export type FulfillmentJob = components['schemas']['FulfillmentJob'];

/**
 * The order read-row (#1409). Bound VERBATIM to the generated
 * `OrderSummary` component — the exact shape the `/api/orders` list
 * projection emits AND the flat `order` payload nested inside
 * `OrderDetail`. Zero hand-written fields: a backend `orderSummarySchema`
 * change surfaces here at typecheck (ADR-0006), and the ~11 provenance /
 * derived columns the old hand-written interface carried but the wire
 * never emitted (billing_address, tax_lines, discount_codes,
 * note_attributes, closed_at, gateway, shipping_lines, source,
 * received_at, hasOnfleetTask, onfleetTaskCount, …) are gone.
 *
 * `line_items` is a single shape — a verbatim Shopify line-item array
 * (`{ [k]: unknown }[]`). The detail flatten seam (schema.tsx
 * `orderResource.api.detail`) no longer re-serialises it to a JSON
 * string (#1222 / #1409).
 */
export type Order = components['schemas']['OrderSummary'];

/**
 * Closed enum of trace event kinds returned by the per-order trace
 * endpoint (`GET /api/orders/:shopifyOrderId/trace`). Exact mirror of
 * the backend `TRACE_EVENT_KIND` as-const map in
 * `backend/src/contexts/commerce/app/order-trace.ts` (#1186/#1187):
 *   - webhook_received — a `webhook_events` row ingressed
 *   - order_projected  — the `orders` row's projection write
 *   - webhook_failure  — a `webhook_failures` row recorded
 *   - operator_action  — an audited per-order operator route
 *   - fulfillment_job  — one per (incl. soft-deleted) `fulfillment_jobs` row
 *   - onfleet_task     — derived from a job whose Onfleet task id is set
 *
 * Declared as an `as const` map so the presentation table
 * (`TraceTimeline`) can pin exhaustiveness over the closed set. This
 * hand-written map has no intrinsic link to the generated OpenAPI wire
 * type (`TraceEvent['kind']`); the mutual-assignability assertion below
 * (`_AssertTraceKindMatchesWire`) supplies that link so a backend add /
 * rename / drop of a kind fails `tsc` here rather than drifting silently
 * into a mis-rendered timeline (EQD §1).
 */
export const TRACE_EVENT_KINDS = {
  WEBHOOK_RECEIVED: 'webhook_received',
  ORDER_PROJECTED: 'order_projected',
  WEBHOOK_FAILURE: 'webhook_failure',
  OPERATOR_ACTION: 'operator_action',
  FULFILLMENT_JOB: 'fulfillment_job',
  ONFLEET_TASK: 'onfleet_task',
} as const;
export type TraceEventKind = (typeof TRACE_EVENT_KINDS)[keyof typeof TRACE_EVENT_KINDS];

/**
 * The `found` trace envelope, sourced verbatim from the generated
 * OpenAPI contract (`GET /api/orders/{shopifyOrderId}/trace`) so a
 * backend schema change surfaces at typecheck rather than as a silent
 * render drift (ADR-0006). `http.get` unwraps the `{data}` success
 * envelope, so the type here is the inner payload.
 */
export type OrderTraceFound = components['schemas']['OrderTrace'];

/** One chronological trace row; `kind` matches `TraceEventKind`. */
export type TraceEvent = OrderTraceFound['events'][number];

/**
 * Compile-time drift guard between the hand-written `TRACE_EVENT_KINDS`
 * map and the generated OpenAPI wire type (`TraceEvent['kind']`). Mutual
 * assignability: if the backend adds, renames, or drops a kind and
 * regenerates the contract, one direction of this assignment stops
 * compiling until `TRACE_EVENT_KINDS` above is updated — the map cannot
 * silently diverge from the wire (same idiom as `lib/errorCodes.ts`).
 */
type _AssertTraceKindMatchesWire = TraceEventKind extends TraceEvent['kind']
  ? TraceEvent['kind'] extends TraceEventKind
    ? true
    : never
  : never;
const _traceKindMatchesWire: _AssertTraceKindMatchesWire = true;
void _traceKindMatchesWire;

export type OrderTraceResponse =
  | OrderTraceFound
  | {
      kind: 'order_not_found';
      shopifyOrderId: string;
    };

/** Result returned by `POST /api/orders/:shopifyOrderId/refetch`. */
export type RefetchResult = components['schemas']['RefetchResult'];

/**
 * Single source of truth for the generated result's closed discriminator.
 * The mutual-assignability assertion makes an OpenAPI kind change fail
 * typecheck until every closed consumer map is updated.
 */
export const REFETCH_RESULT_KINDS = {
  APPLIED: 'applied',
  STALE_EVENT: 'stale_event',
  NOT_FOUND_IN_SHOPIFY: 'not_found_in_shopify',
} as const satisfies Record<string, RefetchResult['kind']>;
type RefetchResultKind = (typeof REFETCH_RESULT_KINDS)[keyof typeof REFETCH_RESULT_KINDS];
type _AssertRefetchKindsMatchWire = RefetchResultKind extends RefetchResult['kind']
  ? RefetchResult['kind'] extends RefetchResultKind
    ? true
    : never
  : never;
const _refetchKindsMatchWire: _AssertRefetchKindsMatchWire = true;
void _refetchKindsMatchWire;

export const orderAPI = {
  async getOrders(params?: ListRequestParams): Promise<ListPage<Order>> {
    return http.get<ListPage<Order>>('/api/orders', {
      params: toApiListParams(params ?? {}, ORDER_FILTERS),
    });
  },

  async getOrder(id: string): Promise<OrderDetail> {
    return http.get<OrderDetail>(`/api/orders/${id}`);
  },

  // Per-order fulfillment-job lookup — replaces the archived
  // `/orders/:id/tasks`. `shopify_order_id` is a virtual filter the
  // delivery read surface resolves to the local order id.
  async getOrderTasks(shopifyId: string): Promise<ListPage<FulfillmentJob>> {
    return http.get<ListPage<FulfillmentJob>>('/api/fulfillment-jobs', {
      params: { shopify_order_id: shopifyId },
    });
  },
};

// Trace path: the canonical caller is the React query hook
// (`features/orders/hooks/useOrderTrace.ts`). The endpoint returns a
// discriminated `OrderTraceResponse` declared above (`kind: 'found'`
// vs `kind: 'order_not_found'`); the hook surfaces the
// `order_not_found` case as a hook-level data state rather than
// throwing — same shape rationale as `useRefetchFromShopify`.

// Re-fetch path: the canonical caller is the React mutation hook
// (`features/orders/hooks/useRefetchFromShopify.ts`). The hook owns
// the URL string and the request shape; surfacing a duplicate
// `orderAPI.refetchFromShopify` here would create a two-URL contract
// in two files that future renames could silently desync (R1 P1
// finding — EQD §1 magic-string risk). The hook consumes the generated
// `RefetchResult` above as its mutation return type.
